import { useState } from 'react';
import PropTypes from 'prop-types';
import { 
    Container, 
    Stack, 
    TextField, 
    Button, 
    Alert,
    Typography,
    Box,
    Backdrop,
    CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { 
    verifyPasswordResetCode, 
    confirmPasswordReset 
} from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import PageHeader from '../../../components/PageHeader';
import { authActions } from '../../modules/auth';

/**
 * PasswordResetHandler - Custom password reset component with dual password entry
 * 
 * Features:
 * - Validates OOB code against Firebase
 * - Dual password entry with confirmation matching
 * - Custom FNI styling and error messaging
 * - Password strength validation
 * - Consistent with central auth application design
 */
export default function PasswordResetHandler({
    oobCode,
    apiKey,
    continueUrl,
    tenantCode,
    onSuccess,
    onError,
    onCancel
}) {
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [codeValidated, setCodeValidated] = useState(false);
    const [userEmail, setUserEmail] = useState('');

    const handleCancel = () => {
        // Use the onCancel prop if provided, otherwise fallback to window redirect
        if (onCancel) {
            onCancel();
        } else {
            // Fallback: redirect to continue URL (if same-origin) or login page
            try {
                const parsed = new URL(continueUrl, window.location.origin);
                if (continueUrl && parsed.origin === window.location.origin) {
                    window.location.href = continueUrl;
                    return;
                }
            } catch { /* invalid URL, fall through */ }
            window.location.href = '/auth/login';
        }
    };

    const styles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
    };

    // Password validation schema
    const validationSchema = yup.object({
        newPassword: yup
            .string()
            .min(12, 'Password must be at least 12 characters')
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]).*$/,
                'Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 digit, and 1 special character'
            )
            .required('New password is required'),
        confirmPassword: yup
            .string()
            .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
            .required('Please confirm your password'),
    });

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!codeValidated) {
                await validateOobCode();
                return;
            }
            await handlePasswordReset(values.newPassword);
        },
    });

    // Step 1: Validate the OOB code
    const validateOobCode = async () => {
        setIsValidating(true);
        try {
            const auth = getAuth();
            const email = await verifyPasswordResetCode(auth, oobCode);
            setUserEmail(email);
            setCodeValidated(true);
        } catch (error) {
            console.error('OOB code validation failed:', error);
            
            let errorMessage = 'Invalid or expired reset link.';
            
            switch (error.code) {
                case 'auth/expired-action-code':
                    errorMessage = 'This password reset link has expired. Please request a new one.';
                    break;
                case 'auth/invalid-action-code':
                    errorMessage = 'This password reset link is invalid. Please request a new one.';
                    break;
                case 'auth/user-disabled':
                    errorMessage = 'This account has been disabled. Please contact support.';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found for this reset link.';
                    break;
                default:
                    errorMessage = `Password reset failed: ${error.message}`;
            }
            
            onError(errorMessage);
        } finally {
            setIsValidating(false);
        }
    };

    // Step 2: Reset the password
    const handlePasswordReset = async (newPassword) => {
        setIsSubmitting(true);
        try {
            const auth = getAuth();
            await confirmPasswordReset(auth, oobCode, newPassword);

            // NVT-41: audit the completed reset. Unauthenticated flow — the
            // backend resolves the real userId from the email. userEmail comes
            // from verifyPasswordResetCode during code validation.
            authActions.recordPasswordEvent({ type: 'RESET_COMPLETED', email: userEmail, tenantCode });

            onSuccess('Your password has been successfully reset. You can now log in with your new password.');
        } catch (error) {
            console.error('Password reset failed:', error);

            // NVT-41: audit a genuine reset failure (weak/reused password or a
            // backend error). Skip link-validity errors (expired/invalid oobCode)
            // — those are a stale entry point, not a password-change attempt, and
            // mirror the GCP-native handler which doesn't audit them.
            if (error?.code !== 'auth/expired-action-code' && error?.code !== 'auth/invalid-action-code') {
                authActions.recordPasswordEvent({ type: 'RESET_FAILED', email: userEmail, tenantCode });
            }

            let errorMessage = 'Failed to reset password. Please try again.';

            switch (error.code) {
                case 'auth/expired-action-code':
                    errorMessage = 'This password reset link has expired. Please request a new one.';
                    break;
                case 'auth/invalid-action-code':
                    errorMessage = 'This password reset link is invalid. Please request a new one.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak. Please choose a stronger password.';
                    break;
                default:
                    errorMessage = `Password reset failed: ${error.message}`;
            }
            
            onError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const showNewPasswordError = Boolean(
        formik.errors.newPassword && 
        formik.touched.newPassword && 
        formik.dirty
    );
    
    const showConfirmPasswordError = Boolean(
        formik.errors.confirmPassword && 
        formik.touched.confirmPassword && 
        formik.dirty
    );

    if (!codeValidated) {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Reset Your Password' variant='h4' />
                
                <Box sx={{ width: '100%', maxWidth: 400, mt: 3 }}>
                    <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                        Click the button below to validate your reset link and proceed with setting a new password.
                    </Typography>
                    
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={validateOobCode}
                        disabled={isValidating}
                        sx={{ py: 1.5, mb: 2 }}
                    >
                        {isValidating ? 'Validating...' : 'Validate Reset Link'}
                    </Button>
                    
                    <Button
                        fullWidth
                        variant="text"
                        onClick={handleCancel}
                        disabled={isValidating}
                    >
                        Cancel
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <>
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Set New Password' variant='h4' />
                
                {userEmail && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Resetting password for: {userEmail}
                    </Typography>
                )}

                <form onSubmit={formik.handleSubmit} style={{ width: '100%', maxWidth: 400 }}>
                    <Stack spacing={3} sx={{ mt: 3 }}>
                        <TextField
                            name='newPassword'
                            label='New Password'
                            type='password'
                            autoFocus
                            value={formik.values.newPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showNewPasswordError}
                            helperText={showNewPasswordError ? formik.errors.newPassword : 'Must be at least 12 characters with 1 uppercase, 1 lowercase, 1 digit, and 1 special character'}
                            variant="outlined"
                            fullWidth
                            disabled={isSubmitting}
                        />

                        <TextField
                            name='confirmPassword'
                            label='Confirm New Password'
                            type='password'
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showConfirmPasswordError}
                            helperText={showConfirmPasswordError ? formik.errors.confirmPassword : 'Re-enter your new password'}
                            variant="outlined"
                            fullWidth
                            disabled={isSubmitting}
                        />

                        <Stack spacing={2} sx={{ mt: 3 }}>
                            <Button
                                type='submit'
                                variant='contained'
                                disabled={isSubmitting || !formik.isValid}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                            </Button>
                            
                            <Button
                                variant='text'
                                onClick={handleCancel}
                                fullWidth
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Container>
            
            <Backdrop
                open={isSubmitting}
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1 
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}

PasswordResetHandler.propTypes = {
    oobCode: PropTypes.string.isRequired,
    apiKey: PropTypes.string,
    continueUrl: PropTypes.string,
    tenantCode: PropTypes.string,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
};
